import {
  CLIENT_PAYMENT_ERRORS,
  type AdminClientFinance,
  type AdminClientInvoice,
  type LedgerMethod,
} from '@carsfinders/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useMemo, useRef, useState } from 'react';

import { Button } from '../../../../components/ui';
import { t } from '../../../../lib/i18n';
import { toast } from '../../../../lib/toast';
import { SHOW_CARD_METHOD } from '../../../finance/topup-flags';
import { apiErrorCode, recordClientPayment } from '../../api';
import {
  evaluateAllocation,
  isPayable,
  parseAmount,
  parsePart,
  paymentAmountChf,
  splitPrefill,
  type DialogCurrency,
  type PaymentTarget,
} from './allocation';
import { formatChf, formatDate, invalidateClientMoney } from './finance-format';
import { DialogShell, FormAlert, fieldClass, labelClass, textareaClass } from './finance-ui';

const METHODS: LedgerMethod[] = SHOW_CARD_METHOD ? ['bank', 'card', 'cash'] : ['bank', 'cash'];

/** Error codes the dialog explains in words; anything else prints its code. */
const EXPLAINED_CODES = new Set<string>([
  CLIENT_PAYMENT_ERRORS.allocationMismatch,
  CLIENT_PAYMENT_ERRORS.receiptRequired,
  CLIENT_PAYMENT_ERRORS.invoiceOverpaid,
  CLIENT_PAYMENT_ERRORS.invoiceNotPayable,
  CLIENT_PAYMENT_ERRORS.invoiceNotFound,
  CLIENT_PAYMENT_ERRORS.paymentsDisabled,
  CLIENT_PAYMENT_ERRORS.fxRateUnavailable,
  CLIENT_PAYMENT_ERRORS.paymentIdConflict,
]);

function errorText(error: unknown): string {
  const code = apiErrorCode(error);
  if (code && EXPLAINED_CODES.has(code)) return t(`clients.finance.payment.errors.${code}`);
  return code
    ? t('clients.finance.payment.errors.failedCode', { code })
    : t('clients.finance.payment.errors.failed');
}

/** `YYYY-MM-DD` of today, local time — the date input's value format. */
function todayInput(now: Date = new Date()): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** The operator's date as an instant: today → now; another day → its local noon. */
function receivedAtOf(dateInput: string): string {
  const now = new Date();
  if (!dateInput || dateInput === todayInput(now)) return now.toISOString();
  const [year, month, day] = dateInput.split('-').map(Number);
  return new Date(year!, month! - 1, day!, 12).toISOString();
}

/** Split fields hold what the operator types; a pre-fill writes plain numbers. */
const partText = (value: number) => (value > 0 ? String(value) : '');

/**
 * «Додати платіж» — the ONE way in for money a client paid (SPEC §4).
 *
 * The operator says how much arrived, in which currency, when and how, and
 * where it goes: the deposit, an invoice, or split between the two. Submit is
 * disabled until the parts add up (`allocationsMatch`, in whole cents — the
 * server's own rule). The server allocates it in ONE transaction.
 *
 * - `paymentId` is minted once per open: a retry after a network failure sends
 *   the same key, so the server answers the recorded result instead of paying
 *   twice.
 * - Invoice / split exist only while `PAYMENTS_ENABLED` is on (`paymentsEnabled`
 *   from the read model); otherwise the dialog says why and allocates to the
 *   deposit.
 * - EUR is offered only with a usable rule-set rate, previewed ≈ CHF with the
 *   contracts `toChf` the server converts with.
 */
export function AddPaymentDialog({
  userId,
  finance,
  initialInvoiceNo,
  onClose,
}: {
  userId: string;
  finance: AdminClientFinance;
  /** «Оплатити» on an invoice row opens the dialog with it preselected. */
  initialInvoiceNo: string | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const nameId = useId();
  const amountRef = useRef<HTMLInputElement>(null);

  const [paymentId] = useState(() => crypto.randomUUID());
  const payable = useMemo(() => finance.invoices.filter(isPayable), [finance.invoices]);
  const invoiceTargets = finance.paymentsEnabled && payable.length > 0;
  const preselected =
    invoiceTargets && initialInvoiceNo
      ? (payable.find((invoice) => invoice.no === initialInvoiceNo) ?? null)
      : null;

  const [amountText, setAmountText] = useState('');
  const [currency, setCurrency] = useState<DialogCurrency>('CHF');
  const [date, setDate] = useState(() => todayInput());
  const [method, setMethod] = useState<LedgerMethod>('bank');
  const [reference, setReference] = useState('');
  const [target, setTarget] = useState<PaymentTarget>(preselected ? 'invoice' : 'deposit');
  const [invoiceNo, setInvoiceNo] = useState<string | null>(
    preselected?.no ?? (invoiceTargets ? payable[0]!.no : null),
  );
  const [invoicePart, setInvoicePart] = useState('');
  const [depositPart, setDepositPart] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    amountRef.current?.focus();
  }, []);

  const amountChf = paymentAmountChf(parseAmount(amountText), currency, finance.fx);
  const invoice = payable.find((candidate) => candidate.no === invoiceNo) ?? null;
  const allocation = evaluateAllocation({
    amountChf,
    target,
    invoiceNo: invoice?.no ?? null,
    outstandingChf: invoice?.outstandingChf ?? null,
    invoicePartChf: parsePart(invoicePart),
    depositPartChf: parsePart(depositPart),
  });
  const referenceMissing = method === 'cash' && reference.trim() === '';

  /** Split re-fills whenever the figure it splits changes: debt first, rest to the deposit. */
  const prefill = (nextAmountChf: number | null, nextInvoice: AdminClientInvoice | null) => {
    if (nextAmountChf === null || !nextInvoice) {
      setInvoicePart('');
      setDepositPart('');
      return;
    }
    const parts = splitPrefill(nextAmountChf, nextInvoice.outstandingChf);
    setInvoicePart(partText(parts.invoiceChf));
    setDepositPart(partText(parts.depositChf));
  };

  const onAmount = (raw: string) => {
    setAmountText(raw);
    if (target === 'split')
      prefill(paymentAmountChf(parseAmount(raw), currency, finance.fx), invoice);
  };
  const onCurrency = (next: DialogCurrency) => {
    setCurrency(next);
    if (target === 'split') {
      prefill(paymentAmountChf(parseAmount(amountText), next, finance.fx), invoice);
    }
  };
  const onTarget = (next: PaymentTarget) => {
    setTarget(next);
    if (next === 'split') prefill(amountChf, invoice);
  };
  const onInvoice = (no: string) => {
    setInvoiceNo(no);
    if (target === 'split')
      prefill(amountChf, payable.find((candidate) => candidate.no === no) ?? null);
  };

  const mutation = useMutation({
    mutationFn: () => {
      const trimmedReference = reference.trim();
      const trimmedNote = note.trim();
      return recordClientPayment(userId, {
        paymentId,
        amount: parseAmount(amountText),
        currency,
        receivedAt: receivedAtOf(date),
        method,
        ...(trimmedReference ? { reference: trimmedReference } : {}),
        ...(trimmedNote ? { note: trimmedNote } : {}),
        allocations: allocation.allocations,
      });
    },
    onSuccess: (result) => {
      toast.success(
        result.replayed
          ? t('clients.finance.payment.replayed')
          : t('clients.finance.payment.recorded'),
      );
      invalidateClientMoney(queryClient, userId);
      onClose();
    },
    onError: (error) => {
      // A stale outstanding / status is the usual cause of a refusal: re-read it.
      if (apiErrorCode(error)) invalidateClientMoney(queryClient, userId);
    },
  });

  const busy = mutation.isPending;
  const canSubmit = allocation.problem === null && !referenceMissing && !busy;
  const allocatedChf = allocation.allocations.reduce((sum, part) => sum + part.amountChf, 0);
  const openRequest = finance.openTopUpRequests[0] ?? null;

  return (
    <DialogShell
      title={t('clients.finance.payment.title')}
      busy={busy}
      onClose={onClose}
      testId="add-payment-dialog"
    >
      {openRequest && (
        <p
          role="note"
          data-testid="payment-open-request"
          className="m-0 rounded-cf-md bg-cf-warn-tint px-3 py-2.5 text-[13px] leading-[1.5] text-cf-ink"
        >
          {t('clients.finance.payment.openRequest', {
            amount: formatChf(openRequest.amountChf),
            date: formatDate(openRequest.createdAt),
          })}
        </p>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_120px]">
        <label className={labelClass}>
          {t('clients.finance.payment.amount')}
          <input
            ref={amountRef}
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={amountText}
            onChange={(event) => onAmount(event.target.value)}
            disabled={busy}
            aria-invalid={amountText !== '' && amountChf === null}
            data-testid="payment-amount"
            className={`${fieldClass} font-mono`}
          />
        </label>
        <label className={labelClass}>
          {t('clients.finance.payment.currency')}
          <select
            value={currency}
            onChange={(event) => onCurrency(event.target.value as DialogCurrency)}
            disabled={busy}
            data-testid="payment-currency"
            className={fieldClass}
          >
            <option value="CHF">CHF</option>
            {finance.fx && <option value="EUR">EUR</option>}
          </select>
        </label>
      </div>
      {currency === 'EUR' && (
        <p
          data-testid="payment-preview"
          className="-mt-1.5 m-0 font-mono text-[13px] text-cf-ink-muted"
        >
          {amountChf === null
            ? finance.fx
              ? '≈ —'
              : t('clients.finance.payment.previewUnavailable')
            : t('clients.finance.payment.preview', { amount: formatChf(amountChf) })}
        </p>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className={labelClass}>
          {t('clients.finance.payment.date')}
          <input
            type="date"
            value={date}
            max={todayInput()}
            onChange={(event) => setDate(event.target.value)}
            disabled={busy}
            data-testid="payment-date"
            className={fieldClass}
          />
        </label>
        <label className={labelClass}>
          {t('clients.finance.payment.method')}
          <select
            value={method}
            onChange={(event) => setMethod(event.target.value as LedgerMethod)}
            disabled={busy}
            data-testid="payment-method"
            className={fieldClass}
          >
            {METHODS.map((option) => (
              <option key={option} value={option}>
                {t(`clients.finance.journal.methods.${option}`)}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className={labelClass}>
        <span>
          {t('clients.finance.payment.reference')}
          {method === 'cash' ? ' *' : ` (${t('clients.finance.payment.referenceOptional')})`}
        </span>
        <input
          type="text"
          value={reference}
          maxLength={64}
          onChange={(event) => setReference(event.target.value)}
          disabled={busy}
          aria-invalid={referenceMissing}
          data-testid="payment-reference"
          className={fieldClass}
        />
        {referenceMissing && (
          <span data-testid="payment-reference-required" className="text-[12px] text-cf-danger">
            {t('clients.finance.payment.referenceRequired')}
          </span>
        )}
      </label>

      <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
        <legend className="mb-1.5 p-0 text-[13px] text-cf-ink-muted">
          {t('clients.finance.payment.target')}
        </legend>
        <div className="flex flex-wrap gap-4">
          {(invoiceTargets
            ? (['deposit', 'invoice', 'split'] as const)
            : (['deposit'] as const)
          ).map((value) => (
            <label key={value} className="inline-flex items-center gap-2 text-[14px] text-cf-ink">
              <input
                type="radio"
                name={`${nameId}-target`}
                value={value}
                checked={target === value}
                onChange={() => onTarget(value)}
                disabled={busy}
                data-testid={`payment-target-${value}`}
                className="h-4 w-4 accent-cf-brand"
              />
              {t(`clients.finance.payment.targets.${value}`)}
            </label>
          ))}
        </div>
        {!finance.paymentsEnabled ? (
          <p
            data-testid="payment-gate-off"
            className="m-0 text-[12px] leading-[1.5] text-cf-ink-subtle"
          >
            {t('clients.finance.payment.gateOff')}
          </p>
        ) : payable.length === 0 ? (
          <p
            data-testid="payment-no-invoices"
            className="m-0 text-[12px] leading-[1.5] text-cf-ink-subtle"
          >
            {t('clients.finance.payment.noInvoices')}
          </p>
        ) : null}
      </fieldset>

      {target !== 'deposit' && invoiceTargets && (
        <label className={labelClass}>
          {t('clients.finance.payment.invoice')}
          <select
            value={invoiceNo ?? ''}
            onChange={(event) => onInvoice(event.target.value)}
            disabled={busy}
            data-testid="payment-invoice"
            className={fieldClass}
          >
            {payable.map((option) => (
              <option key={option.no} value={option.no}>
                {t('clients.finance.payment.invoiceOption', {
                  no: option.no,
                  title: option.lotTitle ?? t(`adminFinance.invoiceKind.${option.kind}`),
                  outstanding: formatChf(option.outstandingChf),
                })}
              </option>
            ))}
          </select>
        </label>
      )}

      {target === 'split' && invoiceTargets && (
        <>
          <p className="m-0 text-[12px] leading-[1.5] text-cf-ink-subtle">
            {t('clients.finance.payment.splitHint')}
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className={labelClass}>
              {t('clients.finance.payment.invoicePart')}
              <input
                type="text"
                inputMode="decimal"
                autoComplete="off"
                value={invoicePart}
                onChange={(event) => setInvoicePart(event.target.value)}
                disabled={busy}
                data-testid="payment-invoice-part"
                className={`${fieldClass} font-mono`}
              />
            </label>
            <label className={labelClass}>
              {t('clients.finance.payment.depositPart')}
              <input
                type="text"
                inputMode="decimal"
                autoComplete="off"
                value={depositPart}
                onChange={(event) => setDepositPart(event.target.value)}
                disabled={busy}
                data-testid="payment-deposit-part"
                className={`${fieldClass} font-mono`}
              />
            </label>
          </div>
          {amountChf !== null && (
            <p
              data-testid="payment-allocated"
              className="m-0 font-mono text-[12px] text-cf-ink-muted"
            >
              {t('clients.finance.payment.allocated', {
                allocated: formatChf(
                  (Number.isFinite(parsePart(invoicePart)) ? parsePart(invoicePart) : 0) +
                    (Number.isFinite(parsePart(depositPart)) ? parsePart(depositPart) : 0),
                ),
                amount: formatChf(amountChf),
              })}
            </p>
          )}
        </>
      )}

      <label className={labelClass}>
        {t('clients.finance.payment.note')}
        <textarea
          value={note}
          onChange={(event) => setNote(event.target.value)}
          maxLength={500}
          rows={2}
          disabled={busy}
          data-testid="payment-note"
          className={textareaClass}
        />
      </label>

      {allocation.problem && allocation.problem !== 'amount' && (
        <p data-testid="payment-problem" className="m-0 text-[12px] text-cf-danger">
          {t(`clients.finance.payment.problems.${allocation.problem}`)}
        </p>
      )}

      {mutation.isError && (
        <FormAlert testId="payment-error">{errorText(mutation.error)}</FormAlert>
      )}

      <div className="mt-1 flex flex-wrap items-center justify-between gap-2">
        <span className="font-mono text-[12px] text-cf-ink-subtle">
          {allocation.problem === null ? formatChf(allocatedChf) : ''}
        </span>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="payment-cancel">
            {t('clients.finance.payment.cancel')}
          </Button>
          <Button
            disabled={!canSubmit}
            onClick={() => mutation.mutate()}
            data-testid="payment-submit"
          >
            {busy ? t('clients.finance.payment.submitting') : t('clients.finance.payment.submit')}
          </Button>
        </div>
      </div>
    </DialogShell>
  );
}
